import { Alert, Skeleton } from 'antd'
import { Fragment, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import ImmersiveDeepZoomStage from './ImmersiveDeepZoomStage'
import { fetchPublicItem, type PublicApiError, type PublicItemDetail } from './publicApi'

/**
 * Item Detail — the P0 acceptance page (spec §11). Structure/class names
 * (`details-layout`/`details-sidebar-sticky`/`details-summary`/
 * `details-block dl`) copied verbatim from Legacy's
 * `templates/public_site/object_detail.html` + `static/css/viewer.css`
 * "details-section" (below the fold) — unchanged this round.
 *
 * The above-the-fold section is Legacy's OpenSeaDragon deep-zoom
 * "viewer-stage" (`ImmersiveDeepZoomStage`, ported 1:1 from
 * `static/js/viewer.js`+`static/css/viewer.css` this round — see that
 * component's own docstring). The old sidebar thumbnail *strip* (per-image
 * selector) is gone — that job now belongs to `viewer-stage`'s own
 * `thumb-list` — but the sidebar still carries Legacy's `details-cover`
 * (a static, non-interactive preview of the cover image, using
 * `cover_url`/`cover_original_url` — never the viewer's zoom state)
 * above the title, matching `object_detail.html`'s
 * `details-sidebar-sticky > img.details-cover + h1`.
 */
export default function PublicItemDetailPage({ workspaceSlug, siteSlug }: { workspaceSlug: string; siteSlug: string }) {
  const { slug = '' } = useParams()
  const [searchParams] = useSearchParams()
  const previewToken = searchParams.get('preview_token')
  const [item, setItem] = useState<PublicItemDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Genuinely-missing-image guard ONLY (spec: never a substitute for a
  // real fix) — e.g. a `cover_asset_id` whose Asset row was hard-deleted
  // out from under a still-published entry. Keyed by the URL itself so a
  // navigation to a different item (new cover URL) always gets a fresh
  // attempt rather than staying permanently "broken" from a prior item.
  const [coverBroken, setCoverBroken] = useState<string | null>(null)
  const base = `/site/${workspaceSlug}/${siteSlug}`

  // ITEM-DETAIL-INITIAL-IMAGE-RACE-P0 — bounded, state-driven revalidation
  // (never a fixed sleep-then-render): the Item Editor's save flow creates
  // the entry *before* its first image finishes uploading, so the very
  // first GET right after creation can legitimately still have an empty
  // gallery/cover for a few seconds. Whenever that happens AND the backend
  // says a browser upload is genuinely still in flight
  // (`image_pending`, see `public_site.py::_image_pending_hint`), poll the
  // same lightweight item-detail endpoint every ~600ms until either an
  // image appears, the hint clears (upload settled — success or failure),
  // or a hard 8s ceiling is hit — never longer, never a silent infinite
  // spin. A revalidation poll's own network error never flips the page to
  // a hard error state; it just stops polling and keeps the last good
  // `item`.
  useEffect(() => {
    setItem(null)
    setError(null)
    setCoverBroken(null)
    let cancelled = false
    let timer: number | undefined
    let deadline = 0

    const hasImage = (d: PublicItemDetail) => d.gallery.length > 0 || !!d.cover_asset_id

    const settle = (data: PublicItemDetail) => {
      if (cancelled) return
      setItem(data)
      if (!hasImage(data) && data.image_pending && Date.now() < deadline) {
        timer = window.setTimeout(revalidate, 600)
      }
    }
    const revalidate = () => {
      fetchPublicItem(workspaceSlug, siteSlug, slug, previewToken)
        .then((r) => settle(r.data))
        .catch(() => {})
    }

    fetchPublicItem(workspaceSlug, siteSlug, slug, previewToken)
      .then((r) => {
        if (cancelled) return
        deadline = Date.now() + 8000
        settle(r.data)
      })
      .catch((err) => {
        if (!cancelled) setError((err as PublicApiError).message)
      })

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [workspaceSlug, siteSlug, slug, previewToken])

  if (error) return <div className="pub-page"><Alert type="error" message={error === 'Not Found' || error === 'item not found' ? '条目未找到' : error} showIcon /></div>
  if (!item) return <div className="pub-page"><Skeleton active paragraph={{ rows: 10 }} /></div>

  const allImages =
    item.gallery.length > 0
      ? item.gallery
      : item.cover_asset_id
        ? [{ asset_id: item.cover_asset_id, caption: '', image_type: '', url: item.cover_url, thumbnail_url: item.cover_url, original_url: item.cover_original_url }]
        : []
  const imageInitializing = allImages.length === 0 && !!item.image_pending

  return (
    <div>
      {previewToken && item.status && item.status !== 'published' && (
        <div className="pub-preview-mode-banner" role="status">
          预览模式 · 该条目尚未发布，仅通过此链接可见（{item.status === 'draft' ? '草稿' : item.status}）
        </div>
      )}
      <ImmersiveDeepZoomStage
        workspaceSlug={workspaceSlug}
        siteSlug={siteSlug}
        images={allImages}
        imageInitializing={imageInitializing}
        title={item.label}
        secondaryTitle={item.secondary_label}
        backHref={`${base}/items`}
      />
      <section className="details-section" id="details-section">
        <div className="details-layout">
          <aside className="details-sidebar">
            <div className="details-sidebar-sticky">
              {(() => {
                const coverSrc = item.cover_url ?? item.cover_original_url ?? undefined
                if (!coverSrc) return null
                if (coverBroken === coverSrc) {
                  // Real fallback placeholder — no broken-image icon, no
                  // alt text rendered in its place, just the same dark
                  // card background `.details-cover` already has.
                  return <div className="details-cover details-cover-placeholder" aria-hidden="true" />
                }
                return (
                  <img
                    className="details-cover"
                    src={coverSrc}
                    alt={item.label}
                    loading="lazy"
                    onError={() => setCoverBroken(coverSrc)}
                  />
                )
              })()}
              <h2 className="details-sidebar-title">{item.label}</h2>
            </div>
          </aside>
          <main className="details-main">
            <div className="details-content">
              <div className="details-summary">
                <h2>{item.label}</h2>
                {item.secondary_label && <p className="original">{item.secondary_label}</p>}
                <div className="meta">
                  {item.accession_number && <span>编号：{item.accession_number}</span>}
                  {item.creator && <span>作者：{item.creator}</span>}
                  {item.era && <span>年代：{item.era}</span>}
                  {item.culture_or_dynasty && <span>朝代/文化：{item.culture_or_dynasty}</span>}
                </div>
                {item.description && <p>{item.description}</p>}
              </div>

              {(item.categories.length > 0 || item.topics.length > 0) && (
                <div className="details-block">
                  <h3>分类与主题</h3>
                  <dl>
                    {item.categories.length > 0 && (
                      <>
                        <dt>条目分类</dt>
                        <dd>
                          {item.categories.map((c) => (
                            <Link key={c.id} to={`${base}/categories/${c.slug}`} style={{ color: '#e1e4e7', marginRight: 8 }}>
                              {c.name}
                            </Link>
                          ))}
                        </dd>
                      </>
                    )}
                    {item.topics.length > 0 && (
                      <>
                        <dt>主题</dt>
                        <dd>
                          {item.topics.map((t) => (
                            <Link key={t.id} to={`${base}/topics/${t.slug}`} style={{ color: '#e1e4e7', marginRight: 8 }}>
                              {t.name}
                            </Link>
                          ))}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              )}

              {(item.material || item.dimension_summary) && (
                <div className="details-block">
                  <h3>材质与尺寸</h3>
                  <dl>
                    {item.material && (
                      <>
                        <dt>材质</dt>
                        <dd>{item.material}</dd>
                      </>
                    )}
                    {item.dimension_summary && (
                      <>
                        <dt>尺寸</dt>
                        <dd>{item.dimension_summary}</dd>
                      </>
                    )}
                    {item.dimensions.map((d, i) => (
                      <Fragment key={i}>
                        <dt>{d.label || '尺寸'}</dt>
                        <dd>
                          {[d.height && `高 ${d.height}`, d.width && `宽 ${d.width}`, d.depth && `深 ${d.depth}`, d.diameter && `直径 ${d.diameter}`]
                            .filter(Boolean)
                            .join(' × ')}
                          {d.unit ? ` ${d.unit}` : ''}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </div>
              )}

              {(item.inscription || item.seal) && (
                <div className="details-block">
                  <h3>题识与钤印</h3>
                  <dl>
                    {item.inscription && (
                      <>
                        <dt>题识</dt>
                        <dd>{item.inscription}</dd>
                      </>
                    )}
                    {item.seal && (
                      <>
                        <dt>钤印</dt>
                        <dd>{item.seal}</dd>
                      </>
                    )}
                  </dl>
                </div>
              )}

              {item.research_notes && (
                <div className="details-block">
                  <h3>研究说明</h3>
                  <p>{item.research_notes}</p>
                </div>
              )}

              {item.provenance.length > 0 && (
                <div className="details-block">
                  <h3>来源</h3>
                  <ul>
                    {item.provenance.map((p, i) => (
                      <li key={i}>
                        {p.date_display} {p.owner_or_source} {p.acquisition_method ? `（${p.acquisition_method}）` : ''}
                        {p.location ? ` · ${p.location}` : ''}
                        {p.notes ? ` — ${p.notes}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {item.publications.length > 0 && (
                <div className="details-block">
                  <h3>出版记录</h3>
                  <ul>
                    {item.publications.map((pub, i) => (
                      <li key={i}>
                        {pub.title}
                        {pub.author ? `，${pub.author}` : ''}
                        {pub.publisher ? `，${pub.publisher}` : ''}
                        {pub.publication_year ? `，${pub.publication_year}` : ''}
                        {pub.page_reference ? `，${pub.page_reference}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {item.exhibition_history.length > 0 && (
                <div className="details-block">
                  <h3>展览记录</h3>
                  <ul>
                    {item.exhibition_history.map((ex, i) => (
                      <li key={i}>
                        {ex.title}
                        {ex.institution ? `，${ex.institution}` : ''}
                        {ex.city ? `（${ex.city}）` : ''}
                        {ex.start_date ? `，${ex.start_date} – ${ex.end_date}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </main>
        </div>
      </section>
    </div>
  )
}
